import {useContext, useState} from "react";
import { useTranslation } from 'react-i18next';
import {AppContext} from "../../context/context";
import {Layer, Shape, Point, Line} from "../../context/context"
import {useEditLayer, useDeleteLayer, useEditLayerTitle, useMoveLayerUp, useMoveLayerDown, useReorderLayers, useMoveShapeToLayer, useMoveShapesToLayer} from "./functions/layerFunctions";
import ShapeIcon from "./shapeIcon";
import ImportGTFSModal from '../map/modals/ImportGTFSModal';

import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Box,
    Stack,
    Typography,
    Menu,
    MenuItem,
    Badge,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from "@mui/material";
import LayersClearOutlinedIcon from "@mui/icons-material/LayersClearOutlined";
import { Icon } from '@liftango/ui-icons';

import EditableTypography from "../base_components/EditableTypography";

import { useDrag, useDrop } from "react-dnd";
import ImportTripsModal from '../map/modals/ImportTripsModal';
import AttributeTableDrawer from './AttributeTableDrawer';
import { format } from 'date-fns';
import PointStyleModal from '../map/modals/PointStyleModal';
import LineStyleModal from '../map/modals/LineStyleModal';
import PolygonStyleModal from '../map/modals/PolygonStyleModal';
import { logEvent } from '../../services/analytics';
import { isFixedRouteLine, FixedRoute } from '../../types/fixedRoutes';
import { isOsmRouteLine, getOsmRouteDefaultColor } from '../../types/osmRoutes';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { formatDistance } from '../../utils/fixedRoutes/distanceFormatter';
import { liftangoBrandTokens } from '../../theme';

type ShapesLayerProps = {
    layerData: Layer;
}

const KEY_POI_TERMS = ['school', 'hospital', 'supermarket', 'library'];
const KEY_POI_ICONS = new Set(['school', 'hospital', 'store']);

const ShapesLayer: React.FC<ShapesLayerProps> = ({
    layerData
}) => {
    const { t } = useTranslation(['mapDialogs', 'common']);
    const context = useContext(AppContext);

    if (!context) {
        throw new Error('Must be used within the context provider')
    }

    const {global} = context;
    const editLayer = useEditLayer();
    const deleteLayer = useDeleteLayer();
    const editLayerTitle = useEditLayerTitle();
    const moveLayerUp = useMoveLayerUp();
    const moveLayerDown = useMoveLayerDown();
    const reorderLayers = useReorderLayers();
    const moveShapeToLayer = useMoveShapeToLayer();
    const moveShapesToLayer = useMoveShapesToLayer();
    const { routes, stops, distanceUnit, clearAllData, deleteRoute, deleteRoutesAndOrphanStops, selectedRouteId, setSelectedRouteId, trafficMultiplier: globalTrafficMultiplier, editingRouteId, enterEditMode, exitEditMode } = useFixedRoutes();
    const missionControl = liftangoBrandTokens.missionControl;
    const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(new Set());
    const [visibleTripCount, setVisibleTripCount] = useState(30);
    const [visibleStopCount, setVisibleStopCount] = useState(30);
    const [visibleRouteCount, setVisibleRouteCount] = useState(30);
    const [visibleKeyLocationCount, setVisibleKeyLocationCount] = useState(30);

    const [accordionIsOpen, setAccordionIsOpen] = useState(layerData.expanded ?? true);
    
    // Get current layer index for drag-and-drop
    const layerIndex = global.mapLayers.findIndex(l => l.layerId === layerData.layerId);
    
    // Make layer draggable
    const [{ isDragging: isLayerDragging }, layerDragRef] = useDrag({
        type: 'LAYER',
        item: { layerId: layerData.layerId, layerIndex },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
    });
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isImportGTFSModalOpen, setIsImportGTFSModalOpen] = useState(false);
    const [shapeContextMenu, setShapeContextMenu] = useState<{ mouseX: number; mouseY: number; shape: Shape } | null>(null);
    const [layerContextMenu, setLayerContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [routeContextMenu, setRouteContextMenu] = useState<{ mouseX: number; mouseY: number; routeLine: Line; route: FixedRoute } | null>(null);
    const [attributeTableOpen, setAttributeTableOpen] = useState(false);
    const [selectedLayerId, setSelectedLayerId] = useState<number>(layerData.layerId);
    const { mapLayers } = global;
    const [selectedShape, setSelectedShape] = useState<Shape | null>(null);
    const [isPointModalOpen, setIsPointModalOpen] = useState(false);
    const [isLineModalOpen, setIsLineModalOpen] = useState(false);
    const [isPolygonModalOpen, setIsPolygonModalOpen] = useState(false);
    const [isDeleteRoutesDialogOpen, setIsDeleteRoutesDialogOpen] = useState(false);
    const [routeToDelete, setRouteToDelete] = useState<{ id: string; name: string } | null>(null);
    const [isDeleteFixedRouteLayerDialogOpen, setIsDeleteFixedRouteLayerDialogOpen] = useState(false);
    const [pendingFixedRouteLayerDelete, setPendingFixedRouteLayerDelete] = useState<{ layerId: number; routeIds: string[] } | null>(null);
    const [isMoveToLayerDialogOpen, setIsMoveToLayerDialogOpen] = useState(false);
    const [shapeToMove, setShapeToMove] = useState<Shape | null>(null);

    // Drop target for shapes (supports both same-layer reordering and cross-layer movement)
    const [{ isOver: isShapeOver }, shapeDropRef] = useDrop<{ shapeIndex: number, layerId: number, shapeData: Shape }, void, { isOver: boolean }>({
        accept: 'PLACE_MARK',
        collect: (monitor) => ({
            isOver: monitor.isOver()
        }),
        drop: (item) => {
            if (item.layerId === layerData.layerId) {
                // Same layer: reorder within layer
                const newShapes = [...layerData.shapes];
                const [movedShape] = newShapes.splice(item.shapeIndex, 1);
                newShapes.splice(layerData.shapes.length, 0, movedShape);
                
                const newLayer = {
                    ...layerData,
                    shapes: newShapes
                };
                editLayer(newLayer, layerData.layerId);
            } else {
                // Different layer: move shape to this layer
                moveShapeToLayer(item.shapeData.id, layerData.layerId);
            }
        }
    });
    
    // Drop target for layers (for reordering)
    const [{ isOver: isLayerOver }, layerDropRef] = useDrop<{ layerId: number, layerIndex: number }, void, { isOver: boolean }>({
        accept: 'LAYER',
        collect: (monitor) => ({
            isOver: monitor.isOver()
        }),
        drop: (item, monitor) => {
            // Prevent nested drops
            if (!monitor.didDrop()) {
                const targetIndex = layerIndex;
                // If dropping on itself, do nothing
                if (targetIndex === item.layerIndex) return;
                // Reorder the layer
                reorderLayers(item.layerIndex, targetIndex);
            }
        }
    });

    const handleToggle = () => {
        setAccordionIsOpen(!accordionIsOpen)
        global.setActiveLayerId(prev => layerData.layerId)
        
        // Track layer accordion toggle
        logEvent({
            category: 'Layer Management',
            action: 'LayerAccordionToggled',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            accordion_expanded: !accordionIsOpen,
            layer_shape_count: layerData.shapes.length
        });
    }
    
    // Handle clicking on layer header to set active layer without toggling accordion
    const handleLayerHeaderClick = (e: React.MouseEvent) => {
        // Only set active layer if clicking on the header itself, not on interactive elements
        if ((e.target as HTMLElement).closest('button, [role="button"], input, textarea')) {
            return; // Don't interfere with buttons, inputs, etc.
        }
        global.setActiveLayerId(layerData.layerId);
        
        // Track active layer selection
        logEvent({
            category: 'Layer Management',
            action: 'ActiveLayerSelected',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            method: 'header_click'
        });
    }

    const handleImportTrips = (points: Point[], lines: Line[]) => {
        global.setActiveLayerId(layerData.layerId);
        const newShapes = [...layerData.shapes, ...points, ...lines];
        const newLayer = {
            ...layerData,
            shapes: newShapes
        };
        editLayer(newLayer, layerData.layerId);
        
        // Track trip import to layer
        logEvent({
            category: 'Layer Management',
            action: 'TripsImportedToLayer',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            points_imported: points.length,
            lines_imported: lines.length,
            total_shapes_after_import: newShapes.length,
            import_type: 'trips'
        });
    };

    const handleImportGTFS = (points: Point[], lines: Line[]) => {
        global.setActiveLayerId(layerData.layerId);
        const newShapes = [...layerData.shapes, ...points, ...lines];
        const newLayer = {
            ...layerData,
            shapes: newShapes
        };
        editLayer(newLayer, layerData.layerId);
        
        // Track GTFS import to layer
        logEvent({
            category: 'Layer Management',
            action: 'GTFSImportedToLayer',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            points_imported: points.length,
            lines_imported: lines.length,
            total_shapes_after_import: newShapes.length,
            import_type: 'gtfs'
        });
    };

    function exportLayerToKML(layerData: Layer) {
        // Helper to escape XML
        const esc = (str: string) =>
            str.replace(/[<>&'\"]/g, (c) =>
                c === '<' ? '&lt;'
                : c === '>' ? '&gt;'
                : c === '&' ? '&amp;'
                : c === '"' ? '&quot;'
                : c === "'" ? '&apos;'
                : c
            );
        let kml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
        kml += `<kml xmlns=\"http://www.opengis.net/kml/2.2\"><Document>\n`;
        for (const shape of layerData.shapes) {
            if (shape.type === 'point') {
                kml += `<Placemark><name>${esc(shape.name||'Point')}</name><Point><coordinates>${shape.coordinates[1]},${shape.coordinates[0]},0</coordinates></Point></Placemark>\n`;
            } else if (shape.type === 'line') {
                kml += `<Placemark><name>${esc(shape.name||'Line')}</name><LineString><coordinates>${shape.coordinates.map(([lat,lon])=>`${lon},${lat},0`).join(' ')}</coordinates></LineString></Placemark>\n`;
            } else if (shape.type === 'polygon') {
                // Only supports first ring
                const coords = shape.coordinates[0]?.map(([lat,lon])=>`${lon},${lat},0`).join(' ');
                kml += `<Placemark><name>${esc(shape.name||'Polygon')}</name><Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>\n`;
            }
        }
        kml += `</Document></kml>`;
        // Download
        const blob = new Blob([kml], {type: 'application/vnd.google-earth.kml+xml'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (layerData.layerName || 'layer') + '.kml';
        document.body.appendChild(a);
        a.click();
        setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);}, 100);
    }

    function exportShapeToKML(shape: Shape) {
        const esc = (str: string) =>
            str.replace(/[<>&'\"]/g, (c) =>
                c === '<' ? '&lt;'
                : c === '>' ? '&gt;'
                : c === '&' ? '&amp;'
                : c === '"' ? '&quot;'
                : c === "'" ? '&apos;'
                : c
            );
        let kml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
        kml += `<kml xmlns=\"http://www.opengis.net/kml/2.2\"><Document>\n`;
        if (shape.type === 'point') {
            kml += `<Placemark><name>${esc(shape.name||'Point')}</name><Point><coordinates>${shape.coordinates[1]},${shape.coordinates[0]},0</coordinates></Point></Placemark>\n`;
        } else if (shape.type === 'line') {
            kml += `<Placemark><name>${esc(shape.name||'Line')}</name><LineString><coordinates>${shape.coordinates.map(([lat,lon])=>`${lon},${lat},0`).join(' ')}</coordinates></LineString></Placemark>\n`;
        } else if (shape.type === 'polygon') {
            const coords = shape.coordinates[0]?.map(([lat,lon])=>`${lon},${lat},0`).join(' ');
            kml += `<Placemark><name>${esc(shape.name||'Polygon')}</name><Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>\n`;
        }
        kml += `</Document></kml>`;
        const blob = new Blob([kml], {type: 'application/vnd.google-earth.kml+xml'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (shape.name || 'shape') + '.kml';
        document.body.appendChild(a);
        a.click();
        setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);}, 100);
    }

    // Helper to zoom to layer
    function zoomToLayer() {
        // Dispatch a custom event with the layer's shapes
        const event = new CustomEvent('zoomToLayer', {
            detail: { shapes: layerData.shapes }
        });
        window.dispatchEvent(event);
        
        // Track zoom to layer
        logEvent({
            category: 'Layer Management',
            action: 'ZoomToLayer',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            layer_shape_count: layerData.shapes.length,
            layer_shape_types: Array.from(new Set(layerData.shapes.map((s: any) => s.type))).join(',')
        });
    }

    // Separate fixed routes from regular trips
    const fixedRouteLines: Line[] = [];
    const trips: { line: Line; origin: Point | null; destination: Point | null }[] = [];
    const usedPointIds = new Set<string>();
    const points: Point[] = layerData.shapes.filter(s => s.type === 'point') as Point[];
    const lines: Line[] = layerData.shapes.filter(s => s.type === 'line') as Line[];

    lines.forEach(line => {
        // Check if it's a fixed route line first
        if (isFixedRouteLine(line)) {
            fixedRouteLines.push(line);
            return;
        }
        
        // Otherwise treat as trip if both IDs are present
        let origin: Point | null = null;
        let destination: Point | null = null;
        if ('originPointId' in line && 'destPointId' in line && line.originPointId && line.destPointId) {
            origin = points.find(p => p.id === line.originPointId) || null;
            destination = points.find(p => p.id === line.destPointId) || null;
            if (origin) usedPointIds.add(origin.id);
            if (destination) usedPointIds.add(destination.id);
            trips.push({ line, origin, destination });
        }
    });

    // Collapse transit-stop points into a single accordion entry so the panel
    // isn't flooded with hundreds of individual rows when stops live alongside
    // other shapes in the host layer.
    //
    // Transit stops arrive from more than one source, each tagged differently:
    // - OSM transit import (population analysis): `creationSource === 'transit-osm-stop'`
    //   (set in src/components/map/handlers/transitAccessibilityHandler.ts).
    // - Spatial-analysis merged layer: `description === 'transit_supply'`
    //   (the source sub-layer role, set in src/components/map/utils/spatialAnalysisGeojson.ts).
    // (GTFS / fixed-route stops are NOT Point shapes — they render as separate
    //  Leaflet markers from FixedRoutesContext via useRenderFixedRouteStopMarkers,
    //  so they never reach this list and cannot be grouped here.)
    const isTransitStopPoint = (p: Point): boolean =>
        p.creationSource === 'transit-osm-stop' || p.description === 'transit_supply';
    const transitStopPoints: Point[] = points.filter(isTransitStopPoint);
    const transitStopIds = new Set(transitStopPoints.map(p => p.id));
    const keyLocationPoints: Point[] = points.filter((p) => {
        if (transitStopIds.has(p.id)) return false;
        // Spatial-analysis Points of Interest (merged-layer shapes tagged with the
        // amenity_anchor role) belong in the accordion, not the flat list — without
        // this they split across both (some matched by name/icon, some not).
        if (p.description === 'amenity_anchor') return true;
        if (p.creationSource === 'osm-key-location') return true;
        if (p.id.startsWith('pa-key-poi-')) return true;
        const lowerName = p.name.toLowerCase();
        const matchesName = KEY_POI_TERMS.some((term) => lowerName.includes(term));
        const matchesIcon = KEY_POI_ICONS.has(p.icon);
        return matchesName || matchesIcon;
    });
    const keyLocationIds = new Set(keyLocationPoints.map(p => p.id));
    // OSM transit routes get the same accordion treatment as stops — keep the
    // panel tidy when an analysis pulls in dozens or hundreds of routes.
    const osmRouteLines: Line[] = lines.filter(isOsmRouteLine);
    const osmRouteIds = new Set(osmRouteLines.map(l => l.id));

    // Only non-trip, non-fixed-route lines, points, and polygons.
    // Also exclude any point grouped into the Transit Stops accordion and any
    // line grouped into the OSM Transit Routes accordion.
    const otherShapes = layerData.shapes.filter(s =>
        (s.type !== 'line' || (
            !isFixedRouteLine(s as Line) &&
            !('originPointId' in s && 'destPointId' in s && s.originPointId && s.destPointId) &&
            !osmRouteIds.has(s.id)
        )) &&
        (!usedPointIds.has(s.id)) &&
        (!transitStopIds.has(s.id)) &&
        (!keyLocationIds.has(s.id))
    );

    const fixedRouteIdsInLayer = Array.from(
        new Set(
            fixedRouteLines
                .map((line) => ((line as any).fixedRouteMetadata?.fixedRouteId as string | undefined))
                .filter((id): id is string => typeof id === 'string' && id.trim() !== '')
        )
    );
    const isFixedRouteLayerByName = layerData.layerName === 'Fixed Routes' || layerData.layerName === 'Optimised Fixed Route Network';
    const shouldConfirmFixedRouteLayerDelete = isFixedRouteLayerByName || fixedRouteIdsInLayer.length > 0;

    const handleShowAttributeTable = () => {
        logEvent({
            category: 'Attribute Table',
            action: 'AttributeTableOpenedFromContextMenu',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            layer_shape_count: layerData.shapes.length,
            layer_shape_types: Array.from(new Set(layerData.shapes.map((s: any) => s.type))).join(','),
            context_menu_type: 'shape' // or 'layer' depending on which context menu
        });
        
        setSelectedLayerId(layerData.layerId);
        setAttributeTableOpen(true);
        setShapeContextMenu(null);
    };

    const handleShowAttributeTableFromLayer = () => {
        logEvent({
            category: 'Attribute Table',
            action: 'AttributeTableOpenedFromContextMenu',
            label: layerData.layerName || 'Unnamed Layer',
            layer_id: layerData.layerId,
            layer_shape_count: layerData.shapes.length,
            layer_shape_types: Array.from(new Set(layerData.shapes.map((s: any) => s.type))).join(','),
            context_menu_type: 'layer'
        });
        
        setSelectedLayerId(layerData.layerId);
        setAttributeTableOpen(true);
        setLayerContextMenu(null);
    };

    // Drop refs go on the wrapper; drag ref goes on the header (drag handle)
    const dropRef = (node: HTMLDivElement | null) => {
        shapeDropRef(node as unknown as HTMLDivElement);
        layerDropRef(node as unknown as HTMLDivElement);
    };

    // Drag handle ref for the AccordionSummary
    const dragHandleRef = (node: HTMLDivElement | null) => {
        layerDragRef(node);
    };

    return (
        <div ref={dropRef}>
            <Accordion
                expanded={accordionIsOpen}
                onChange={handleToggle}
                // Disable the collapse animation for large layers — animating hundreds
                // of shape rows causes noticeable jank when closing the accordion.
                TransitionProps={layerData.shapes.length > 30 ? { timeout: 0 } : undefined}
                sx={{
                    margin: ["1px", "5px", "3px", "5px"],
                    boxShadow: 3,
                    border: global.activeLayerId === layerData.layerId ? "2px solid #1976d2" : (isLayerOver ? "2px dashed #1976d2" : "none"),
                    "&:before": { display: "none" },
                    opacity: isLayerDragging ? 0.5 : 1,
                    backgroundColor: isLayerOver ? 'action.hover' : 'transparent',
                    transition: 'opacity 0.2s ease, border 0.2s ease, background-color 0.2s ease',
                }}
            >
                <AccordionSummary
                    ref={dragHandleRef}
                    component="div"
                    expandIcon={<Icon name="chevron-down" size={20} />}
                    aria-controls={`layer-content-${layerData.layerId}`}
                    id={`layer-header-${layerData.layerId}`}
                    onClick={handleLayerHeaderClick}
                    onContextMenu={e => {
                        e.preventDefault();
                        setLayerContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
                    }}
                    sx={{ cursor: 'move' }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <EditableTypography 
                                setValueFunction={editLayerTitle(layerData)}
                                variant="h6"
                                textAlign="left"
                            >
                                {layerData.layerName}
                            </EditableTypography>
                        </div>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Toggle visibility
                                    const updatedLayer = { ...layerData, hidden: !layerData.hidden };
                                    editLayer(updatedLayer, layerData.layerId);
                                    
                                    // Track layer visibility toggle
                                    logEvent({
                                        category: 'Layer Management',
                                        action: 'LayerVisibilityChanged',
                                        label: layerData.layerName || 'Unnamed Layer',
                                        layer_id: layerData.layerId,
                                        layer_visible: !layerData.hidden,
                                        layer_shape_count: layerData.shapes.length
                                    });
                                }}
                            >
                                {layerData.hidden ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
                            </IconButton>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    exportLayerToKML(layerData);
                                    
                                    // Track layer export
                                    logEvent({
                                        category: 'Layer Management',
                                        action: 'LayerExported',
                                        label: layerData.layerName || 'Unnamed Layer',
                                        layer_id: layerData.layerId,
                                        export_format: 'kml',
                                        layer_shape_count: layerData.shapes.length,
                                        layer_shape_types: Array.from(new Set(layerData.shapes.map((s: any) => s.type))).join(',')
                                    });
                                }}
                            >
                                <Icon size={20} name="file-arrow-down" />
                            </IconButton>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (shouldConfirmFixedRouteLayerDelete) {
                                        setPendingFixedRouteLayerDelete({
                                            layerId: layerData.layerId,
                                            routeIds: fixedRouteIdsInLayer,
                                        });
                                        setIsDeleteFixedRouteLayerDialogOpen(true);
                                        return;
                                    }
                                    deleteLayer(layerData.layerId);
                                }}
                            >
                                <Icon size={20} name="trash" />
                            </IconButton>
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails>
                    <Box sx={{paddingTop: "10px"}}>
                        <Stack spacing={1}>
                            {/* Render fixed routes as accordions */}
                            {fixedRouteLines
                                .filter((routeLine) => {
                                    // Only show routes that still exist in the routes context
                                    const metadata = (routeLine as any).fixedRouteMetadata;
                                    if (metadata?.fixedRouteId) {
                                        return routes.some(r => r.id === metadata.fixedRouteId);
                                    }
                                    return false;
                                })
                                .sort((a, b) => {
                                    const aMeta = (a as any).fixedRouteMetadata;
                                    const bMeta = (b as any).fixedRouteMetadata;
                                    const aRoute = routes.find(r => r.id === aMeta?.fixedRouteId);
                                    const bRoute = routes.find(r => r.id === bMeta?.fixedRouteId);

                                    const aName = (aRoute?.name || a.name || aMeta?.routeName || '').trim();
                                    const bName = (bRoute?.name || b.name || bMeta?.routeName || '').trim();
                                    const byName = aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true });
                                    if (byName !== 0) return byName;

                                    const aShort = (aRoute?.shortName || aMeta?.shortName || '').trim();
                                    const bShort = (bRoute?.shortName || bMeta?.shortName || '').trim();
                                    return aShort.localeCompare(bShort, undefined, { sensitivity: 'base', numeric: true });
                                })
                                .map((routeLine) => {
                                const metadata = (routeLine as any).fixedRouteMetadata;
                                const route = routes.find(r => r.id === metadata?.fixedRouteId);
                                const isExpanded = expandedRoutes.has(routeLine.id);
                                
                                return (
                                    <Accordion 
                                        key={routeLine.id} 
                                        expanded={isExpanded}
                                        onChange={() => {
                                            const newExpanded = new Set(expandedRoutes);
                                            if (isExpanded) {
                                                newExpanded.delete(routeLine.id);
                                            } else {
                                                newExpanded.add(routeLine.id);
                                            }
                                            setExpandedRoutes(newExpanded);
                                        }}
                                        sx={{ boxShadow: 1, mb: 1 }}
                                    >
                                        <AccordionSummary 
                                            component="div"
                                            expandIcon={<Icon name="chevron-down" size={20} />}
                                            onContextMenu={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (route) {
                                                    setRouteContextMenu({ 
                                                        mouseX: e.clientX - 2, 
                                                        mouseY: e.clientY - 4, 
                                                        routeLine,
                                                        route 
                                                    });
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <ShapeIcon shapeData={routeLine} layerId={layerData.layerId} showName={false} />
                                                    <Box>
                                                        <Typography variant="subtitle2">
                                                            {routeLine.name || metadata?.routeName || t('mapDialogs:shapes.unnamedRoute')}
                                                        </Typography>
                                                        {metadata?.shortName && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                                                                {metadata.shortName}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {metadata?.fixedRouteId && route && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                if (editingRouteId === route.id) {
                                                                    exitEditMode();
                                                                } else {
                                                                    if (route.stops.length >= 2) {
                                                                        enterEditMode(route.id);
                                                                    } else {
                                                                        alert(t('mapDialogs:shapes.editGeometryAlert'));
                                                                    }
                                                                }
                                                            }}
                                                            title={editingRouteId === route.id ? t('mapDialogs:shapes.exitEditMode') : t('mapDialogs:shapes.editRouteGeometry')}
                                                            sx={{ 
                                                                color: editingRouteId === route.id ? 'primary.main' : 'text.secondary'
                                                            }}
                                                        >
                                                            {editingRouteId === route.id ? <Icon name="lock" size={20} /> : <Icon name="lock" size={20} />}
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            const newShapes = layerData.shapes.map(s =>
                                                                s.id === routeLine.id ? { ...s, hidden: !s.hidden } : s
                                                            );
                                                            editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                        }}
                                                    >
                                                        {routeLine.hidden ? <Icon name="eye-slash" size={20} /> : <Icon name="eye" size={20} />}
                                                    </IconButton>
                                                    {metadata?.fixedRouteId && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                if (route) {
                                                                    setRouteToDelete({ id: route.id, name: route.name });
                                                                }
                                                            }}
                                                            sx={{ color: 'error.main' }}
                                                        >
                                                            <Icon name="trash" size={20} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {route && (() => {
                                                // Get route-specific traffic multiplier or fall back to global
                                                const routeTrafficMultiplier = route.trafficMultiplier ?? globalTrafficMultiplier;
                                                
                                                return (
                                                <Stack spacing={1.5}>
                                                    {route.stops.map((stopConfig, idx) => {
                                                        const stop = stops.find(s => s.id === stopConfig.stopId);
                                                        const stopName = stop?.name || t('mapDialogs:shapes.stopN', { n: idx + 1 });
                                                        const waitTime = stopConfig.waitTimeMinutes;
                                                        
                                                        // Get travel time and distance to this stop (from previous stop)
                                                        const segment = idx > 0 
                                                            ? route.segments.find(
                                                                (seg) => seg.fromStopId === route.stops[idx - 1].stopId && seg.toStopId === stopConfig.stopId
                                                            )
                                                            : undefined;
                                                        // Apply traffic multiplier to travel times for display
                                                        const baseTravelTime = segment?.travelTimeMinutes;
                                                        const travelTime = baseTravelTime !== undefined
                                                            ? Math.round(baseTravelTime * routeTrafficMultiplier)
                                                            : undefined;
                                                        const distance = segment?.distanceMeters;
                                                        
                                                        const isLast = idx === route.stops.length - 1;
                                                        
                                                        // For circulator routes, check if there's a return segment from last stop to first stop
                                                        const returnSegment = route.isCirculator && isLast && route.stops.length >= 2
                                                            ? route.segments.find(
                                                                (seg) => seg.fromStopId === stopConfig.stopId && seg.toStopId === route.stops[0].stopId
                                                            )
                                                            : undefined;
                                                        // Apply traffic multiplier to return travel time for display
                                                        const baseReturnTravelTime = returnSegment?.travelTimeMinutes;
                                                        const returnTravelTime = baseReturnTravelTime !== undefined
                                                            ? Math.round(baseReturnTravelTime * routeTrafficMultiplier)
                                                            : undefined;
                                                        const returnDistance = returnSegment?.distanceMeters;
                                                        
                                                        return (
                                                            <Box key={stopConfig.stopId} sx={{ pb: 1.5, borderBottom: idx < route.stops.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                                                {/* Stop Row */}
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    gap: 2,
                                                                    mb: 0.5
                                                                }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                        <Chip
                                                                            label={idx + 1}
                                                                            size="small"
                                                                            sx={{ 
                                                                                minWidth: 32,
                                                                                height: 24,
                                                                                fontWeight: 600
                                                                            }}
                                                                        />
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500,
                                                                                textAlign: 'left'
                                                                            }}
                                                                        >
                                                                            {stopName}
                                                                        </Typography>
                                                                    </Box>
                                                                    {waitTime > 0 && (
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            color="text.secondary"
                                                                            sx={{
                                                                                whiteSpace: 'nowrap',
                                                                                fontWeight: 500
                                                                            }}
                                                                        >
                                                                            {t('mapDialogs:shapes.waitMin', { value: waitTime })}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                
                                                                {/* Travel/Return Info */}
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'space-between',
                                                                    pl: 5.5, // Align with stop name (chip width + gap)
                                                                    gap: 2
                                                                }}>
                                                                    {!isLast && travelTime !== undefined && (
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            color="text.secondary"
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                fontWeight: 500
                                                                            }}
                                                                        >
                                                                            <span>→</span>
                                                                            <span>{t('mapDialogs:shapes.travelMin', { value: travelTime })}</span>
                                                                            {distance !== undefined && (
                                                                                <span>({formatDistance(distance, distanceUnit)})</span>
                                                                            )}
                                                                        </Typography>
                                                                    )}
                                                                    {route.isCirculator && isLast && returnTravelTime !== undefined && (
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            color="primary.main"
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                fontWeight: 600
                                                                            }}
                                                                        >
                                                                            <span>→</span>
                                                                            <span>{t('mapDialogs:shapes.returnMin', { value: returnTravelTime })}</span>
                                                                            {returnDistance !== undefined && (
                                                                                <span>({formatDistance(returnDistance, distanceUnit)})</span>
                                                                            )}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                                );
                                            })()}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                            {/* Render up to 30 trip accordions; a footer handles the remainder.
                                Capping rendered items at 30 keeps React reconciliation fast —
                                1200+ Accordion nodes block the main thread for ~500 ms on toggle. */}
                            {trips.slice(0, visibleTripCount).map((trip, idx) => (
                                <Accordion
                                    key={trip.line.id}
                                    sx={{
                                        boxShadow: '0 4px 14px rgba(58,58,72,0.08)',
                                        border: '1px solid rgba(58,58,72,0.10)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.92)',
                                        mb: 1,
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary component="div" expandIcon={<Icon name="chevron-down" size={20} />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ShapeIcon shapeData={trip.line} layerId={layerData.layerId} showName={false} />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ color: 'text.primary', fontWeight: 700, textAlign: 'left' }}
                                                >
                                                    {(!trip.line.name || trip.line.name === "Trip")
                                                        ? t('mapDialogs:shapes.tripN', { n: idx + 1 })
                                                        : trip.line.name}
                                                </Typography>
                                                <Box sx={{ ml: 0.5, display: 'flex', gap: 1 }}>
                                                    <Box
                                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: missionControl.tripPickup }}
                                                        aria-label={t('mapDialogs:shapes.standardPassengers', { count: Number(trip.line.standard_seat ?? trip.line.numPassengers ?? 0) })}
                                                        title={t('mapDialogs:shapes.standardPassengersTitle')}
                                                    >
                                                        <Icon size={15} name="user" />
                                                        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600, fontSize: '0.85rem' }}>
                                                            {trip.line.standard_seat ?? trip.line.numPassengers ?? 0}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: missionControl.tripDropoff }}
                                                        aria-label={t('mapDialogs:shapes.wheelchairPassengers', { count: Number(trip.line.wheelchair ?? 0) })}
                                                        title={t('mapDialogs:shapes.wheelchairPassengersTitle')}
                                                    >
                                                        <Icon size={15} name="wheelchair-move" />
                                                        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600, fontSize: '0.85rem' }}>
                                                            {trip.line.wheelchair ?? 0}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {/* Visibility Icon */}
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        // Toggle visibility for trip line and its points
                                                        const idsToToggle = [
                                                            trip.line.id,
                                                            trip.origin?.id,
                                                            trip.destination?.id
                                                        ].filter(Boolean);
                                                        const newShapes = layerData.shapes.map(s =>
                                                            idsToToggle.includes(s.id)
                                                                ? { ...s, hidden: !s.hidden }
                                                                : s
                                                        );
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    {(trip.line.hidden && trip.origin?.hidden && trip.destination?.hidden)
                                                        ? <Icon name="eye-slash" size={20} />
                                                        : <Icon name="eye" size={20} />}
                                                </IconButton>
                                                {/* Delete Icon */}
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        // Delete trip line and its points if not used elsewhere
                                                        const isPointUsedElsewhere = (pointId: string | undefined) =>
                                                            !!layerData.shapes.find(s =>
                                                                s.type === 'line' &&
                                                                s.id !== trip.line.id &&
                                                                'originPointId' in s && 'destPointId' in s &&
                                                                (s.originPointId === pointId || s.destPointId === pointId)
                                                            );
                                                        const idsToDelete = [
                                                            trip.line.id,
                                                            (!isPointUsedElsewhere(trip.origin?.id) ? trip.origin?.id : undefined),
                                                            (!isPointUsedElsewhere(trip.destination?.id) ? trip.destination?.id : undefined)
                                                        ].filter(Boolean);
                                                        const newShapes = layerData.shapes.filter(s => !idsToDelete.includes(s.id));
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    <Icon name="trash" size={20} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0.5 }}>
                                        <Stack spacing={1}>
                                            {trip.origin && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.86)', border: '1px solid rgba(58,58,72,0.08)' }}>
                                                    <ShapeIcon shapeData={trip.origin} layerId={layerData.layerId} showName={false} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {trip.origin.name && trip.origin.name !== "Origin"
                                                            ? t('mapDialogs:shapes.originLabelNamed', { name: trip.origin.name })
                                                            : t('mapDialogs:shapes.originLabel')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        [{trip.origin.coordinates[0].toFixed(5)}, {trip.origin.coordinates[1].toFixed(5)}]
                                                    </Typography>
                                                </Box>
                                            )}
                                            {trip.destination && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.86)', border: '1px solid rgba(58,58,72,0.08)' }}>
                                                    <ShapeIcon shapeData={trip.destination} layerId={layerData.layerId} showName={false} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {trip.destination.name && trip.destination.name !== "Destination"
                                                            ? t('mapDialogs:shapes.destinationLabelNamed', { name: trip.destination.name })
                                                            : t('mapDialogs:shapes.destinationLabel')}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        [{trip.destination.coordinates[0].toFixed(5)}, {trip.destination.coordinates[1].toFixed(5)}]
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                            {trips.length > visibleTripCount && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 2,
                                        border: '1px dashed rgba(58,58,72,0.15)',
                                        backgroundColor: 'rgba(255,255,255,0.60)',
                                        mt: 0.5,
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {t('mapDialogs:shapes.moreTrips', { value: (trips.length - visibleTripCount).toLocaleString() })}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setVisibleTripCount(c => c + 30)}
                                        >
                                            {t('mapDialogs:shapes.viewMore')}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => setVisibleTripCount(trips.length)}
                                        >
                                            {t('mapDialogs:shapes.viewAll')}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            {/* Grouped OSM Transit Routes accordion: collapses N route lines
                                into one entry, mirroring the Transit Stops grouping below. */}
                            {osmRouteLines.length > 0 && (
                                <Accordion
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    sx={{
                                        boxShadow: '0 4px 14px rgba(58,58,72,0.08)',
                                        border: '1px solid rgba(58,58,72,0.10)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.92)',
                                        mb: 1,
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary component="div" expandIcon={<Icon name="chevron-down" size={20} />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ShapeIcon shapeData={osmRouteLines[0]} layerId={layerData.layerId} showName={false} />
                                                <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700, textAlign: 'left' }}>
                                                    {t('mapDialogs:shapes.transitRoutes', { value: osmRouteLines.length.toLocaleString() })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const allHidden = osmRouteLines.every(l => l.hidden);
                                                        const newShapes = layerData.shapes.map(s =>
                                                            osmRouteIds.has(s.id) ? { ...s, hidden: !allHidden } : s
                                                        );
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    {osmRouteLines.every(l => l.hidden)
                                                        ? <Icon name="eye-slash" size={20} />
                                                        : <Icon name="eye" size={20} />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const newShapes = layerData.shapes.filter(s => !osmRouteIds.has(s.id));
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    <Icon name="trash" size={20} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0.5 }}>
                                        <Stack spacing={0.5}>
                                            {osmRouteLines.slice(0, visibleRouteCount).map((route) => {
                                                const meta = route.osmRouteMetadata!;
                                                const swatchColor = meta.colour || getOsmRouteDefaultColor(meta.mode);
                                                return (
                                                    <Box
                                                        key={route.id}
                                                        onContextMenu={e => {
                                                            e.preventDefault();
                                                            setShapeContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4, shape: route });
                                                        }}
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.86)', border: '1px solid rgba(58,58,72,0.08)' }}
                                                    >
                                                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: swatchColor, flexShrink: 0, border: '1px solid rgba(0,0,0,0.15)' }} />
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {route.name}
                                                            </Typography>
                                                            {(meta.operator || meta.from || meta.to) && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {[meta.operator, meta.from && meta.to ? `${meta.from} → ${meta.to}` : null].filter(Boolean).join(' · ')}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                const newShapes = layerData.shapes.map(s =>
                                                                    s.id === route.id ? { ...s, hidden: !s.hidden } : s
                                                                );
                                                                editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                            }}
                                                        >
                                                            {route.hidden ? <Icon name="eye-slash" size={20} /> : <Icon name="eye" size={20} />}
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                const newLayer = { ...layerData, shapes: layerData.shapes.filter(s => s.id !== route.id) };
                                                                editLayer(newLayer, layerData.layerId);
                                                            }}
                                                        >
                                                            <Icon name="trash" size={20} />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            })}
                                            {osmRouteLines.length > visibleRouteCount && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 1.5,
                                                        py: 1,
                                                        borderRadius: 2,
                                                        border: '1px dashed rgba(58,58,72,0.15)',
                                                        backgroundColor: 'rgba(255,255,255,0.60)',
                                                        mt: 0.5,
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('mapDialogs:shapes.moreRoutes', { value: (osmRouteLines.length - visibleRouteCount).toLocaleString() })}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                        <Button size="small" onClick={() => setVisibleRouteCount(c => c + 30)}>{t('mapDialogs:shapes.viewMore')}</Button>
                                                        <Button size="small" onClick={() => setVisibleRouteCount(osmRouteLines.length)}>{t('mapDialogs:shapes.viewAll')}</Button>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                            {/* Grouped Transit Stops accordion: collapses N stop points
                                into one entry to avoid flooding the panel. Top-level eye/trash
                                act on the whole group; each inner row keeps its own controls. */}
                            {transitStopPoints.length > 0 && (
                                <Accordion
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    sx={{
                                        boxShadow: '0 4px 14px rgba(58,58,72,0.08)',
                                        border: '1px solid rgba(58,58,72,0.10)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.92)',
                                        mb: 1,
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary component="div" expandIcon={<Icon name="chevron-down" size={20} />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ShapeIcon shapeData={transitStopPoints[0]} layerId={layerData.layerId} showName={false} />
                                                <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700, textAlign: 'left' }}>
                                                    {t('mapDialogs:shapes.transitStops', { value: transitStopPoints.length.toLocaleString() })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const allHidden = transitStopPoints.every(p => p.hidden);
                                                        const newShapes = layerData.shapes.map(s =>
                                                            transitStopIds.has(s.id) ? { ...s, hidden: !allHidden } : s
                                                        );
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    {transitStopPoints.every(p => p.hidden)
                                                        ? <Icon name="eye-slash" size={20} />
                                                        : <Icon name="eye" size={20} />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const newShapes = layerData.shapes.filter(s => !transitStopIds.has(s.id));
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    <Icon name="trash" size={20} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0.5 }}>
                                        <Stack spacing={0.5}>
                                            {transitStopPoints.slice(0, visibleStopCount).map((stop) => (
                                                <Box
                                                    key={stop.id}
                                                    onContextMenu={e => {
                                                        e.preventDefault();
                                                        setShapeContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4, shape: stop });
                                                    }}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.86)', border: '1px solid rgba(58,58,72,0.08)' }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                        <ShapeIcon shapeData={stop} layerId={layerData.layerId} />
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            const newShapes = layerData.shapes.map(s =>
                                                                s.id === stop.id ? { ...s, hidden: !s.hidden } : s
                                                            );
                                                            editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                        }}
                                                    >
                                                        {stop.hidden ? <Icon name="eye-slash" size={20} /> : <Icon name="eye" size={20} />}
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            const newLayer = { ...layerData, shapes: layerData.shapes.filter(s => s.id !== stop.id) };
                                                            editLayer(newLayer, layerData.layerId);
                                                        }}
                                                    >
                                                        <Icon name="trash" size={20} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            {transitStopPoints.length > visibleStopCount && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 1.5,
                                                        py: 1,
                                                        borderRadius: 2,
                                                        border: '1px dashed rgba(58,58,72,0.15)',
                                                        backgroundColor: 'rgba(255,255,255,0.60)',
                                                        mt: 0.5,
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('mapDialogs:shapes.moreStops', { value: (transitStopPoints.length - visibleStopCount).toLocaleString() })}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                        <Button size="small" onClick={() => setVisibleStopCount(c => c + 30)}>{t('mapDialogs:shapes.viewMore')}</Button>
                                                        <Button size="small" onClick={() => setVisibleStopCount(transitStopPoints.length)}>{t('mapDialogs:shapes.viewAll')}</Button>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                            {/* Grouped non-transit key locations accordion: schools, hospitals,
                                supermarkets, and libraries. */}
                            {keyLocationPoints.length > 0 && (
                                <Accordion
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    sx={{
                                        boxShadow: '0 4px 14px rgba(58,58,72,0.08)',
                                        border: '1px solid rgba(58,58,72,0.10)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.92)',
                                        mb: 1,
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary component="div" expandIcon={<Icon name="chevron-down" size={20} />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ShapeIcon shapeData={keyLocationPoints[0]} layerId={layerData.layerId} showName={false} />
                                                <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700, textAlign: 'left' }}>
                                                    {t('mapDialogs:shapes.keyLocations', { value: keyLocationPoints.length.toLocaleString() })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const allHidden = keyLocationPoints.every(p => p.hidden);
                                                        const newShapes = layerData.shapes.map(s =>
                                                            keyLocationIds.has(s.id) ? { ...s, hidden: !allHidden } : s
                                                        );
                                                        editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                    }}
                                                >
                                                    {keyLocationPoints.every(p => p.hidden)
                                                        ? <Icon name="eye-slash" size={20} />
                                                        : <Icon name="eye" size={20} />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const newLayer = {
                                                            ...layerData,
                                                            shapes: layerData.shapes.filter(s => !keyLocationIds.has(s.id))
                                                        };
                                                        editLayer(newLayer, layerData.layerId);
                                                    }}
                                                >
                                                    <Icon name="trash" size={20} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0.5 }}>
                                        <Stack spacing={0.5}>
                                            {keyLocationPoints.slice(0, visibleKeyLocationCount).map((keyLocation) => (
                                                <Stack key={keyLocation.id} direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                                        <ShapeIcon shapeData={keyLocation} layerId={layerData.layerId} />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                const newShapes = layerData.shapes.map(s =>
                                                                    s.id === keyLocation.id ? { ...s, hidden: !s.hidden } : s
                                                                );
                                                                editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                            }}
                                                        >
                                                            {keyLocation.hidden ? <Icon name="eye-slash" size={20} /> : <Icon name="eye" size={20} />}
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                const newLayer = { ...layerData, shapes: layerData.shapes.filter(s => s.id !== keyLocation.id) };
                                                                editLayer(newLayer, layerData.layerId);
                                                            }}
                                                        >
                                                            <Icon name="trash" size={20} />
                                                        </IconButton>
                                                    </Box>
                                                </Stack>
                                            ))}
                                            {keyLocationPoints.length > visibleKeyLocationCount && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 1.5,
                                                        py: 1,
                                                        borderRadius: 2,
                                                        border: '1px dashed rgba(58,58,72,0.15)',
                                                        backgroundColor: 'rgba(255,255,255,0.60)',
                                                        mt: 0.5,
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('mapDialogs:shapes.moreKeyLocations', { value: (keyLocationPoints.length - visibleKeyLocationCount).toLocaleString() })}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                        <Button size="small" onClick={() => setVisibleKeyLocationCount(c => c + 30)}>{t('mapDialogs:shapes.viewMore')}</Button>
                                                        <Button size="small" onClick={() => setVisibleKeyLocationCount(keyLocationPoints.length)}>{t('mapDialogs:shapes.viewAll')}</Button>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                            {/* Render other shapes as before */}
                            {otherShapes.map((shape, index) => (
                                <div
                                    key={shape.id}
                                    onContextMenu={e => {
                                        e.preventDefault();
                                        setShapeContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4, shape });
                                    }}
                                    style={{ width: '100%' }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                            <ShapeIcon 
                                                shapeData={shape} 
                                                layerId={layerData.layerId}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    // Toggle shape visibility
                                                    const newShapes = layerData.shapes.map(s =>
                                                        s.id === shape.id ? { ...s, hidden: !s.hidden } : s
                                                    );
                                                    editLayer({ ...layerData, shapes: newShapes }, layerData.layerId);
                                                }}
                                            >
                                                {shape.hidden ? <Icon name="eye-slash" size={20} /> : <Icon name="eye" size={20} />}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    // Delete shape
                                                    const newLayer = {
                                                        ...layerData,
                                                        shapes: layerData.shapes.filter(s => s.id !== shape.id)
                                                    };
                                                    editLayer(newLayer, layerData.layerId);
                                                }}
                                            >
                                                <Icon name="trash" size={20} />
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                </div>
                            ))}
                        </Stack>
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Shape context menu for export and zoom */}
            <Menu
                open={!!shapeContextMenu}
                onClose={() => setShapeContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    shapeContextMenu ? { top: shapeContextMenu.mouseY, left: shapeContextMenu.mouseX } : undefined
                }
            >
                <MenuItem
                    onClick={() => {
                        if (shapeContextMenu) {
                            setSelectedShape(shapeContextMenu.shape);
                            if (shapeContextMenu.shape.type === 'point') setIsPointModalOpen(true);
                            else if (shapeContextMenu.shape.type === 'line') setIsLineModalOpen(true);
                            else if (shapeContextMenu.shape.type === 'polygon') setIsPolygonModalOpen(true);
                        }
                        setShapeContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.editStyle')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (shapeContextMenu) window.dispatchEvent(new CustomEvent('zoomToShape', { detail: { shape: shapeContextMenu.shape } }));
                        setShapeContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.zoomToShape')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (shapeContextMenu) exportShapeToKML(shapeContextMenu.shape);
                        setShapeContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.exportShape')}
                </MenuItem>
                <MenuItem
                    onClick={handleShowAttributeTable}
                >
                    {t('mapDialogs:shapes.showAttributeTable')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (shapeContextMenu) {
                            setShapeToMove(shapeContextMenu.shape);
                            setIsMoveToLayerDialogOpen(true);
                        }
                        setShapeContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.moveToLayer')}
                </MenuItem>
            </Menu>
            <Menu
                open={!!layerContextMenu}
                onClose={() => setLayerContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    layerContextMenu ? { top: layerContextMenu.mouseY, left: layerContextMenu.mouseX } : undefined
                }
            >
                <MenuItem
                    onClick={() => {
                        zoomToLayer();
                        setLayerContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.zoomToLayer')}
                </MenuItem>
                <MenuItem
                    onClick={handleShowAttributeTableFromLayer}
                >
                    {t('mapDialogs:shapes.showAttributeTable')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        moveLayerUp(layerData.layerId);
                        setLayerContextMenu(null);
                    }}
                    disabled={layerIndex <= 0}
                >
                    {t('mapDialogs:shapes.moveUp')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        moveLayerDown(layerData.layerId);
                        setLayerContextMenu(null);
                    }}
                    disabled={layerIndex < 0 || layerIndex >= global.mapLayers.length - 1}
                >
                    {t('mapDialogs:shapes.moveDown')}
                </MenuItem>
                {layerData.layerName === 'Fixed Routes' && (
                    <MenuItem
                        onClick={() => {
                            setIsDeleteRoutesDialogOpen(true);
                            setLayerContextMenu(null);
                        }}
                        sx={{ color: 'error.main' }}
                    >
                        <Icon size={18} name="trash" style={{ marginRight: 8}} />
                        {t('mapDialogs:shapes.clearAllRoutes')}
                    </MenuItem>
                )}
            </Menu>
            {/* Route context menu */}
            <Menu
                open={!!routeContextMenu}
                onClose={() => setRouteContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    routeContextMenu ? { top: routeContextMenu.mouseY, left: routeContextMenu.mouseX } : undefined
                }
            >
                <MenuItem
                    onClick={() => {
                        if (routeContextMenu) {
                            window.dispatchEvent(new CustomEvent('zoomToShape', {
                                detail: { shape: routeContextMenu.routeLine }
                            }));

                            logEvent({
                                category: 'Layer Management',
                                action: 'ZoomToRouteFromContextMenu',
                                label: routeContextMenu.route.name || 'Unnamed Route',
                                route_id: routeContextMenu.route.id,
                            });
                        }
                        setRouteContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.zoomToShape')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (routeContextMenu) {
                            window.dispatchEvent(new CustomEvent('fixedRouteLineClick', {
                                detail: {
                                    routeId: routeContextMenu.route.id,
                                    routeName: routeContextMenu.route.name
                                }
                            }));

                            logEvent({
                                category: 'Route Management',
                                action: 'EditRouteFromContextMenu',
                                label: routeContextMenu.route.name || 'Unnamed Route',
                                route_id: routeContextMenu.route.id,
                            });
                        }
                        setRouteContextMenu(null);
                    }}
                >
                    {t('mapDialogs:shapes.editRoute')}
                </MenuItem>
            </Menu>
            {/* Delete All Routes Confirmation Dialog */}
            <Dialog
                open={isDeleteRoutesDialogOpen}
                onClose={() => setIsDeleteRoutesDialogOpen(false)}
            >
                <DialogTitle>{t('mapDialogs:shapes.confirmTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('mapDialogs:shapes.confirmAllRoutesBody')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteRoutesDialogOpen(false)}>
                        {t('common:cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            clearAllData();
                            setIsDeleteRoutesDialogOpen(false);
                        }}
                        color="error"
                        variant="contained"
                    >
                        {t('mapDialogs:shapes.clearAllRoutes')}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Delete Fixed-Route Layer Confirmation Dialog */}
            <Dialog
                open={isDeleteFixedRouteLayerDialogOpen}
                onClose={() => {
                    setIsDeleteFixedRouteLayerDialogOpen(false);
                    setPendingFixedRouteLayerDelete(null);
                }}
            >
                <DialogTitle>{t('mapDialogs:shapes.deleteFixedRouteLayerTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('mapDialogs:shapes.deleteFixedRouteLayerBody', { count: pendingFixedRouteLayerDelete?.routeIds.length ?? 0 })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsDeleteFixedRouteLayerDialogOpen(false);
                            setPendingFixedRouteLayerDelete(null);
                        }}
                    >
                        {t('common:cancel')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            const pending = pendingFixedRouteLayerDelete;
                            if (!pending) return;
                            if (pending.routeIds.length > 0) {
                                deleteRoutesAndOrphanStops(pending.routeIds);
                            }
                            deleteLayer(pending.layerId);
                            setIsDeleteFixedRouteLayerDialogOpen(false);
                            setPendingFixedRouteLayerDelete(null);
                        }}
                    >
                        {t('mapDialogs:shapes.deleteLayerAndRoutes')}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Delete Individual Route Confirmation Dialog */}
            <Dialog
                open={!!routeToDelete}
                onClose={() => setRouteToDelete(null)}
            >
                <DialogTitle>{t('mapDialogs:shapes.deleteRouteTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('mapDialogs:shapes.deleteRouteBody', { name: routeToDelete?.name ?? '' })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRouteToDelete(null)}>
                        {t('common:cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            if (routeToDelete) {
                                deleteRoute(routeToDelete.id);
                                // Clear selected route if it's the one being deleted
                                if (selectedRouteId === routeToDelete.id) {
                                    setSelectedRouteId(null);
                                }
                                setRouteToDelete(null);
                            }
                        }}
                        color="error"
                        variant="contained"
                    >
                        {t('mapDialogs:common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
            <AttributeTableDrawer
                open={attributeTableOpen}
                onClose={() => setAttributeTableOpen(false)}
                layers={mapLayers}
                selectedLayerId={selectedLayerId}
                onLayerChange={setSelectedLayerId}
            />
            {/* Style modals */}
            <PointStyleModal
                open={isPointModalOpen}
                onClose={() => setIsPointModalOpen(false)}
                point={selectedShape && selectedShape.type === 'point' ? selectedShape : null}
                global={global}
                editLayer={editLayer}
                setSelectedPoint={p => setSelectedShape(p)}
                setIsPointModalOpen={setIsPointModalOpen}
            />
            <LineStyleModal
                open={isLineModalOpen}
                onClose={() => setIsLineModalOpen(false)}
                line={selectedShape && selectedShape.type === 'line' ? selectedShape : null}
                global={global}
                editLayer={editLayer}
                setSelectedLine={l => setSelectedShape(l)}
                setIsLineModalOpen={setIsLineModalOpen}
            />
            <PolygonStyleModal
                open={isPolygonModalOpen}
                onClose={() => setIsPolygonModalOpen(false)}
                polygon={selectedShape && selectedShape.type === 'polygon' ? selectedShape : null}
                global={global}
                editLayer={editLayer}
                setSelectedPolygon={p => setSelectedShape(p)}
                setIsPolygonModalOpen={setIsPolygonModalOpen}
            />
            {/* Move to Layer Dialog */}
            <Dialog
                open={isMoveToLayerDialogOpen}
                onClose={() => {
                    setIsMoveToLayerDialogOpen(false);
                    setShapeToMove(null);
                }}
            >
                <DialogTitle>{t('mapDialogs:shapes.moveToLayerTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {t('mapDialogs:shapes.moveToLayerPrompt', { name: shapeToMove?.name || t('mapDialogs:shapes.moveToLayerShapeFallback') })}
                    </Typography>
                    <Stack spacing={1}>
                        {mapLayers
                            .filter(layer => layer.layerId !== layerData.layerId)
                            .map(layer => (
                                <Button
                                    key={layer.layerId}
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => {
                                        if (shapeToMove) {
                                            moveShapeToLayer(shapeToMove.id, layer.layerId);
                                        }
                                        setIsMoveToLayerDialogOpen(false);
                                        setShapeToMove(null);
                                    }}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                >
                                    {layer.layerName || t('mapDialogs:shapes.layerFallback', { id: layer.layerId })}
                                </Button>
                            ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setIsMoveToLayerDialogOpen(false);
                        setShapeToMove(null);
                    }}>
                        {t('common:cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default ShapesLayer
